import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the EmploymentTypeMasterContext
const EmploymentTypeMasterContext = createContext();

// Custom hook to use the EmploymentTypeMasterContext
export const useEmploymentType = () => useContext(EmploymentTypeMasterContext);

// Provider component to wrap the app
export const EmploymentTypeMasterProvider = ({ children }) => {
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const API_URL =
    "https://hrmspayroll.prepstripe.com/api/masters/employment-types"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEmploymentTypes = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setEmploymentTypes(response.data?.data || {}); // Access `data.data` for actual departments master
      const { data: employmentTypesArray, current_page, last_page, per_page, total } = response.data.data;

      setEmploymentTypes(employmentTypesArray || []); 

      setPagination({
        currentPage: current_page,
        totalPages: last_page,
        totalItems: total,
        perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching employment-type master:", error);
      setError(
        error.response?.data?.message || "Failed to fetch employee types.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments master from the API on load
  useEffect(() => {
    fetchEmploymentTypes();
  }, [API_URL, token]);

  // Add a new employment-type
  const addEmploymentType = async (newEmploymentType) => {
    try {
      const response = await axios.post(API_URL, newEmploymentType, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setEmploymentTypes((prev) => [...prev, response.data?.data]);
      setEmploymentTypes((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding employment-type:", error);
    }
  };

  // Update an existing employment-type
  const updateEmploymentType = async (updatedEmploymentType) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedEmploymentType.id}`,
        updatedEmploymentType,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );
      setEmploymentTypes((prev) =>
        prev.map((employmentType) =>
          employmentType.id === updatedEmploymentType.id
            ? { ...employmentType, ...response.data?.data }
            : employmentType,
        ),
      );
    } catch (error) {
      console.error("Error updating employment-type master:", error);
    }
  };

  // Delete a employment-type
  const deleteEmploymentType = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setEmploymentTypes((prev) =>
        prev.filter((employmentType) => employmentType.id !== id),
      );
    } catch (error) {
      console.error("Error deleting employment-type:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      employmentTypes,
      addEmploymentType,
      updateEmploymentType,
      deleteEmploymentType,
      fetchEmploymentTypes,
    }),
    [employmentTypes, isLoading, pagination], // Recalculate the value only when `employment-type` changes
  );

  return (
    <EmploymentTypeMasterContext.Provider value={value}>
      {children}
    </EmploymentTypeMasterContext.Provider>
  );
};
